import { FORM_ERROR } from 'final-form';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { Field, Form } from 'react-final-form';
import { connect } from 'react-redux';
import {
  ALL_INITIATIVE_TYPES,
  ALL_SECTIONS,
  NO_PORTFOLIO,
  buildDuplicatePayload,
  duplicationText,
} from '../../../constants/projectDuplication.js';
import { getOrgTermsContent } from '../../../content/utils.js';
import { mapFinalFormErrors } from '../../../lib/utils.js';
import {
  retrievePortfolios,
  selectPortfolios,
} from '../../../modules/portfolio.js';
import { duplicateProject } from '../../../modules/projectDuplications.js';
import { retrievePlanStatus } from '../../../modules/projects.js';
import DatePickerAdapter from '../../DatePickerAdapter.jsx';
import FieldGroup from '../../FieldGroup.jsx';
import { FormError } from '../../FormError.jsx';
import WhatToCopyField from './WhatToCopyField.jsx';
import styles from './DuplicateProjectModal.module.css';

const DEFAULT_ERROR = 'Failed to duplicate project';
const mapServerErrors = mapFinalFormErrors(DEFAULT_ERROR);

// 5xx and network errors come as plain strings without a message.
const mapErrors = (error) =>
  error && typeof error === 'object' && error.message
    ? mapServerErrors(error)
    : { [FORM_ERROR]: DEFAULT_ERROR };

const validate = (values) => {
  const errors = {};

  if (!values.name?.trim()) errors.name = duplicationText('nameRequired');
  if (!values.startDate)
    errors.startDate = duplicationText('startDateRequired');
  if (!values.endDate) errors.endDate = duplicationText('endDateRequired');

  if (
    values.startDate &&
    values.endDate &&
    values.endDate.isBefore(values.startDate, 'day')
  ) {
    errors.endDate = duplicationText('endBeforeStart');
  }

  return errors;
};

const mapStateToProps = (state) => ({
  portfolios: selectPortfolios(state),
});

const mapDispatchToProps = {
  duplicateProject,
  retrievePlanStatus,
  retrievePortfolios,
};

const DuplicateProjectModal = ({
  project,
  show,
  onHide,
  portfolios,
  duplicateProject,
  retrievePlanStatus,
  retrievePortfolios,
}) => {
  const [planLimitReached, setPlanLimitReached] = useState(false);

  useEffect(() => {
    retrievePlanStatus()
      .then(({ canCreateProject }) => setPlanLimitReached(!canCreateProject))
      .catch(() => setPlanLimitReached(false));

    if (!portfolios.length) retrievePortfolios();
  }, []);

  const initialValues = useMemo(
    () => ({
      name: `${duplicationText('namePrefix')} ${project.name}`,
      startDate: null,
      endDate: null,
      portfolioId: project.portfolioId
        ? String(project.portfolioId)
        : NO_PORTFOLIO,
      sections: ALL_SECTIONS,
      initiativeTypes: ALL_INITIATIVE_TYPES,
    }),
    [project.id]
  );

  // The source portfolio stays selectable even when the list does not include it.
  const portfolioOptions = useMemo(() => {
    const options = portfolios.map(({ id, name }) => ({ id, name }));

    if (
      project.portfolioId &&
      !options.some(({ id }) => id === project.portfolioId)
    ) {
      options.unshift({
        id: project.portfolioId,
        name: project.portfolioName || '',
      });
    }

    return options;
  }, [portfolios, project.portfolioId, project.portfolioName]);

  const onSubmit = (values) =>
    duplicateProject(project.id, buildDuplicatePayload(values))
      .then(onHide)
      .catch(mapErrors);

  return (
    <Modal show={show} onHide={onHide} dialogClassName={styles.modal}>
      <Form
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validate}
        render={({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit} noValidate>
            <Modal.Header closeButton className={styles.modalHeader}>
              <Modal.Title>{duplicationText('title')}</Modal.Title>
              <div className={styles.source}>
                {duplicationText('source')} <strong>{project.name}</strong>
                {project.archived
                  ? ` ${duplicationText('archivedSourceNote')}`
                  : null}
              </div>
            </Modal.Header>

            <Modal.Body>
              {planLimitReached ? (
                <p className="text-danger">
                  {getOrgTermsContent('limitedNewProjectMessage')}
                  <a href="mailto:pricing@changesync.com">
                    pricing@changesync.com
                  </a>
                  .
                </p>
              ) : null}

              <Field
                id="duplicateProjectName"
                name="name"
                label={duplicationText('nameLabel')}
                type="text"
                component={FieldGroup}
              />

              <Field
                id="duplicateProjectPortfolio"
                name="portfolioId"
                label={duplicationText('portfolioLabel')}
                componentClass="select"
                component={FieldGroup}
              >
                <option value={NO_PORTFOLIO}>
                  {duplicationText('noPortfolio')}
                </option>
                {portfolioOptions.map(({ id, name }) => (
                  <option key={id} value={String(id)}>
                    {name}
                  </option>
                ))}
              </Field>

              <Row>
                <Col sm={6}>
                  <Field
                    id="duplicateProjectStartDate"
                    name="startDate"
                    label="Start Date"
                    placeholderText="Click to select"
                    component={DatePickerAdapter}
                    isClearable
                    allowNull
                  />
                </Col>
                <Col sm={6}>
                  <Field
                    id="duplicateProjectEndDate"
                    name="endDate"
                    label="End Date"
                    placeholderText="Click to select"
                    component={DatePickerAdapter}
                    isClearable
                    allowNull
                  />
                </Col>
              </Row>

              <WhatToCopyField />

              {submitError ? <FormError submitError={submitError} /> : null}
            </Modal.Body>

            <Modal.Footer>
              <Row>
                <Col sm={6}>
                  <Button block disabled={submitting} onClick={onHide}>
                    Cancel
                  </Button>
                </Col>
                <Col sm={6}>
                  <Button
                    block
                    bsStyle="primary"
                    type="submit"
                    disabled={submitting || planLimitReached}
                  >
                    {duplicationText('submit')}
                  </Button>
                </Col>
              </Row>
            </Modal.Footer>
          </form>
        )}
      />
    </Modal>
  );
};

DuplicateProjectModal.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    archived: PropTypes.bool,
    portfolioId: PropTypes.number,
    portfolioName: PropTypes.string,
  }).isRequired,
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DuplicateProjectModal);
